import { HelpCircleIcon, ListChecksIcon, LoaderCircleIcon, PencilIcon, SendIcon, TriangleAlertIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useDebounceValue } from 'usehooks-ts'

import useAppNavigation from 'apps/employer_renewals/hooks/useAppNavigation'
import useRenewalEmployees from 'apps/employer_renewals/hooks/useRenewalEmployees'
import RenewalEmployeesTableFilters from 'apps/employer_renewals/pages/employees/eligibility/RenewalEmployeesTableFilters'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import { useEmployerRenewalConfig } from 'apps/employer_renewals/providers/EmployerRenewalConfigProvider'
import { EmployerRenewalsRoute } from 'apps/employer_renewals/routes'
import { Banner, Button, Checkbox, IconButton, Pagination, Table, TextButton, Tooltip } from 'slab'
import { cn, getFormattedDate } from 'utils'
import type {
  EmployersRenewalsEmployee,
  EmployersRenewalsEmployeeEnrollmentStatus,
  GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEmploymentTypesItem as EmploymentTypeFilter,
  GetEmployersRenewalsRenewalConfigsIdEmployeesFilterEnrollmentStatusesItem as EnrollmentStatusFilter,
} from 'utils/web_api.schemas'

import EditEmployeeSheet from './EditEmployeeSheet'
import EligibilityExplanationModal from './EligibilityExplanationModal'
import EnrollmentStatusBadge from './EnrollmentStatusBadge'
import { employmentTypeDisplayValue } from './utils'

const PAGE_SIZE = 10
const REQUIRED_INFORMATION_ENROLLMENT_STATUSES = ['enrolled', 'not_accepted', 'declined']

const InviteStatusDependentWarningIcon = ({ employeeStatus }: { employeeStatus: EmployersRenewalsEmployeeEnrollmentStatus }) => {
  const color = REQUIRED_INFORMATION_ENROLLMENT_STATUSES.includes(employeeStatus) ? '#ef4444' : '#eab308'
  return <TriangleAlertIcon size={16} color={color} />
}

export default function RenewalEmployeesTable ({ shakeMissingInfoBanner = false }: { shakeMissingInfoBanner?: boolean }) {
  const [pageIndex, setPageIndex] = useState(0)
  const [search, setSearch] = useState('')
  const [enrollmentStatuses, setEnrollmentStatuses] = useState<EnrollmentStatusFilter[]>([])
  const [employmentTypes, setEmploymentTypes] = useState<EmploymentTypeFilter[]>([])
  const [explanationModalOpen, setExplanationModalOpen] = useState(false)
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | undefined>(undefined)
  const [autoNavigateToNext, setAutoNavigateToNext] = useState(false)
  const [excludeOffboardedEmployees, setExcludeOffboardedEmployees] = useState(true)
  const [hoveredEmployeeId, setHoveredEmployeeId] = useState<string | undefined>(undefined)

  const [debouncedSearch] = useDebounceValue(search, 300)

  const { navigateTo } = useAppNavigation()

  useEffect(() => {
    setPageIndex(0)
  }, [debouncedSearch, enrollmentStatuses, employmentTypes])

  const {
    employees,
    eligibleEmployeeIds,
    isLoading: isLoadingEmployees,
    error: employeeError,
    pagination,
    updateEmployee,
    isMarkingAllEligible,
    markAllEligible,
    refetchEmployees,
    refetchEmployeeMetadata,
    numOffboardedEmployees,
    employeeIdsInvitedInCurrentYearWithMissingRequiredInfo,
  } = useRenewalEmployees({
    search: debouncedSearch,
    enrollmentStatuses,
    employmentTypes,
    excludeOffboardedEmployees,
    pageIndex,
    pageSize: PAGE_SIZE,
    includeEmployeesWithMissingRequiredInfo: true,
  })
  const { employerIsALE } = useEmployer()
  const { employerRenewalConfig } = useEmployerRenewalConfig()

  const hasMissingRequiredInfo = useCallback((employee: EmployersRenewalsEmployee): boolean => {
    return !employee.date_of_birth || !employee.zip || (!employee.yearly_pay && !!employerIsALE)
  }, [employerIsALE])

  const eligibilityCheckboxTooltipContent = useCallback((employee: EmployersRenewalsEmployee): string => {
    const missingInformation = []
    if (!employee.date_of_birth) {
      missingInformation.push('date of birth')
    }
    if (!employee.zip) {
      missingInformation.push('zip code')
    }
    if (!employee.yearly_pay && !!employerIsALE) {
      missingInformation.push('annual salary')
    }

    return `In order to mark this employee as eligible, please add the following information to the employee by clicking their name in the table, or uploading a new census: ${missingInformation.join(', ')}`
  }, [employerIsALE])

  const onEligibleChange = (employeeId: string, eligible: boolean) => {
    updateEmployee(employeeId, { eligible })
  }

  const handleSelectEmployeeFromTable = useCallback((employee: EmployersRenewalsEmployee) => {
    setSelectedEmployeeId(employee.id)
    setAutoNavigateToNext(false)
  }, [])

  const handleSelectEmployeeFromBanner = useCallback(() => {
    // Find the first employee with missing info
    const firstEmployeeId = employeeIdsInvitedInCurrentYearWithMissingRequiredInfo[0]
    if (firstEmployeeId) {
      setSelectedEmployeeId(firstEmployeeId)
      setAutoNavigateToNext(true)
    }
  }, [employeeIdsInvitedInCurrentYearWithMissingRequiredInfo])

  const handleSuccessfulSave = useCallback(async () => {
    // Find the updated employee and check if they should be automatically marked as eligible
    const updatedEmployee = employees.find(e => e.id === selectedEmployeeId)
    if (updatedEmployee) {
      if (REQUIRED_INFORMATION_ENROLLMENT_STATUSES.includes(updatedEmployee.enrollment_status)) {
        await updateEmployee(updatedEmployee.id, { eligible: true })
      }
    }

    // Refetch to get the updated list of employees with missing info
    // this will re-render the table with the updated sorting, as well as update the missing info banner
    const [_freshEmployees, freshMetadata] = await Promise.all([
      await refetchEmployees(),
      await refetchEmployeeMetadata(),
    ])

    // If not in auto-navigate mode, just close the sheet
    if (!autoNavigateToNext || !freshMetadata) {
      setSelectedEmployeeId(undefined)
      return
    }

    const updatedEmployeeIdsWithMissingInfo = freshMetadata.employee_ids_invited_last_year_with_missing_info

    // If there are no more employees with missing info, close the sheet
    if (updatedEmployeeIdsWithMissingInfo.length === 0) {
      setSelectedEmployeeId(undefined)
      toast.success('All required information has been submitted for all employees!')
      return
    }

    // Move to the next employee with missing info
    const nextEmployeeId = updatedEmployeeIdsWithMissingInfo[0]
    setSelectedEmployeeId(nextEmployeeId)
  }, [autoNavigateToNext, selectedEmployeeId, employees, updateEmployee, refetchEmployees, refetchEmployeeMetadata])

  if (isLoadingEmployees) {
    return (
      <div className='flex flex-col items-center justify-center gap-2 h-full p-10'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
        <div className='text-sm text-gray-500'>Loading employees...</div>
      </div>
    )
  }

  if (employeeError) {
    return (
      <div className='p-10'>
        <Banner type='critical' title='Error loading employees'>
          {employeeError instanceof Error && employeeError.message}
        </Banner>
      </div>
    )
  }

  return (
    <>
      <EditEmployeeSheet
        onClose={() => setSelectedEmployeeId(undefined)}
        prospectEmployeeId={selectedEmployeeId}
        onSuccess={handleSuccessfulSave}
        autoNavigateToNext={autoNavigateToNext}
      />
      <div className='flex flex-col bg-white rounded-2xl border border-gray-200 shadow-sm'>
        <div className='p-4 border border-blue-300 bg-blue-50 rounded-t-2xl'>
          <div className='flex items-center gap-2 justify-between flex-wrap'>
            <div className='flex items-center gap-2'>
              <SendIcon size={16} className='text-blue-400 shrink-0' />
              <span className='body-bold'>{eligibleEmployeeIds.size} eligible employees will be invited on Nov 5 to enroll.</span>
            </div>
            <TextButton
              color='gray'
              size='small'
              leftIcon={HelpCircleIcon}
              onClick={() => { setExplanationModalOpen(true) }}
            >
              What does "eligible" mean?
            </TextButton>
          </div>
        </div>
        <div className='flex flex-row gap-4 p-4 justify-between'>
          <div className='flex-1'>
            <RenewalEmployeesTableFilters
              search={search}
              enrollmentStatuses={enrollmentStatuses}
              employmentTypes={employmentTypes}
              onSearchChange={setSearch}
              onEnrollmentStatusesChange={setEnrollmentStatuses}
              onEmploymentTypesChange={setEmploymentTypes}
              excludeOffboardedEmployees={excludeOffboardedEmployees}
              onExcludeOffboardedEmployeesChange={setExcludeOffboardedEmployees}
              numOffboardedEmployees={numOffboardedEmployees}
              trailingComponent={
                <div>
                  <Button
                    variant='tertiary'
                    size='small'
                    leftIcon={ListChecksIcon}
                    disabled={isMarkingAllEligible}
                    onClick={() => { markAllEligible() }}
                  >
                    Select all
                  </Button>
                  <Button
                    variant='tertiary'
                    size='small'
                    leftIcon={PencilIcon}
                    onClick={() => navigateTo(EmployerRenewalsRoute.EMPLOYEES_CENSUS, true)}
                  >
                    Edit employee census
                  </Button>
                </div>
            }
            />
          </div>
        </div>
        {employeeIdsInvitedInCurrentYearWithMissingRequiredInfo.length > 0 && (
          <div className='px-4 pb-4'>
            <div className={cn('transition-transform origin-top duration-500', shakeMissingInfoBanner ? 'animate-shake' : '')}>
              <Banner
                type='critical'
                title={
                  <>
                    {employeeIdsInvitedInCurrentYearWithMissingRequiredInfo.length} employee{employeeIdsInvitedInCurrentYearWithMissingRequiredInfo.length > 1 ? 's were' : ' was'} invited to enroll this year but {employeeIdsInvitedInCurrentYearWithMissingRequiredInfo.length > 1 ? 'are' : 'is'} missing required information
                  </>
                }
              >
                Please update their information in order to mark them as eligible for {employerRenewalConfig?.renewal_year} coverage
                <div className='flex items-center gap-4 mt-4'>
                  <Button
                    variant='secondary'
                    size='small'
                    onClick={handleSelectEmployeeFromBanner}
                  >
                    Update employee information
                  </Button>
                  {/* <TextButton
                    color='gray'
                    size='small'
                    onClick={() => { navigateTo(EmployerRenewalsRoute.EMPLOYEES_CENSUS) }}
                  >
                    Update employee information via CSV upload
                  </TextButton> */}
                </div>
              </Banner>
            </div>
          </div>
        )}
        <Table>
          <Table.Head>
            <Table.Row>
              <Table.ColumnHeader align='center' sticky>
                <div className='flex items-center justify-center'>
                  Eligible?
                  <IconButton
                    icon={HelpCircleIcon}
                    label='What does "eligible" mean?'
                    onClick={() => { setExplanationModalOpen(true) }}
                  />
                </div>
              </Table.ColumnHeader>
              <Table.ColumnHeader sticky>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Employment type</Table.ColumnHeader>
              <Table.ColumnHeader>2025 status</Table.ColumnHeader>
              <Table.ColumnHeader>Annual salary</Table.ColumnHeader>
              <Table.ColumnHeader>Zip code</Table.ColumnHeader>
              <Table.ColumnHeader>Date of birth</Table.ColumnHeader>
            </Table.Row>
          </Table.Head>
          <Table.Body>
            {employees.map((employee) => (
              <Table.Row
                key={employee.id}
                onClick={() => handleSelectEmployeeFromTable(employee)}
                onMouseEnter={() => setHoveredEmployeeId(employee.id)}
                onMouseLeave={() => setHoveredEmployeeId(undefined)}
                accessibleLabel={`Edit ${employee.name}`}
              >
                <Table.Cell align='center' sticky>
                  <label>
                    <Tooltip
                      content={eligibilityCheckboxTooltipContent(employee)}
                      enabled={hasMissingRequiredInfo(employee)}
                      delayDuration={0}
                    >
                      <Checkbox
                        aria-label='Eligible'
                        checked={eligibleEmployeeIds.has(employee.id)}
                        disabled={hasMissingRequiredInfo(employee)}
                        onChange={() => {
                          onEligibleChange(employee.id, !eligibleEmployeeIds.has(employee.id))
                        }}
                      />
                    </Tooltip>
                  </label>
                </Table.Cell>
                <Table.Cell sticky>
                  <div className='flex items-center'>
                    <span className={cn('sensitive', hoveredEmployeeId === employee.id ? 'underline' : '')}>
                      {employee.name}
                    </span>
                    {hoveredEmployeeId === employee.id && (
                      <PencilIcon size={16} className='text-gray-500 absolute right-2' />
                    )}
                  </div>
                </Table.Cell>
                <Table.Cell>
                  <span>
                    {employee.employment_type ? employmentTypeDisplayValue(employee.employment_type) : '-'}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  <span>
                    {employee.enrollment_status
                      ? <EnrollmentStatusBadge enrollmentStatus={employee.enrollment_status} />
                      : '-'}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {employee.yearly_pay?.formatted_amount ?? (
                    employerIsALE === false ? '-' : <InviteStatusDependentWarningIcon employeeStatus={employee.enrollment_status} />
                  )}
                </Table.Cell>
                <Table.Cell>
                  {employee.zip ?? <InviteStatusDependentWarningIcon employeeStatus={employee.enrollment_status} />}
                </Table.Cell>
                <Table.Cell>
                  {employee.date_of_birth ? getFormattedDate(employee.date_of_birth) : <InviteStatusDependentWarningIcon employeeStatus={employee.enrollment_status} />}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        <div className='flex justify-end p-2'>
          <Pagination
            pageIndex={pageIndex}
            perPage={PAGE_SIZE}
            totalRows={pagination?.total_records ?? 0}
            onPageChange={setPageIndex}
          />
        </div>
        <EligibilityExplanationModal
          open={explanationModalOpen}
          onOpenChange={setExplanationModalOpen}
        />
      </div>
    </>
  )
}
